import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  constructor() {
    super();
    this.animationTimeout = undefined;
    this.cleanupTimeout = undefined;
    this.preloadImage = this.preloadImage.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.animationTimeout) clearTimeout(this.animationTimeout);
    if (this.cleanupTimeout) clearTimeout(this.cleanupTimeout);
    this.removeEventListener('pointerenter', this.preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';
    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.animateFlyToCart();
    }
  }

  preloadImage() {
    const image = this.dataset.productVariantMedia;
    if (!image) return;
    preloadImage(image);
  }

  animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.animationTimeout) clearTimeout(this.animationTimeout);
    if (this.cleanupTimeout) clearTimeout(this.cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.animationTimeout = setTimeout(() => {
      this.cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  constructor() {
    super();
    this.abortController = new AbortController();
    this.timeout = undefined;

    // bind methods
    this.onVariantUpdate = this.onVariantUpdate.bind(this);
    this.onVariantSelected = this.onVariantSelected.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.abortController.abort();
  }

  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    event.preventDefault();

    if (this.timeout) clearTimeout(this.timeout);

    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: { ...fetchCfg.headers, Accept: 'text/html' },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          this.setLiveRegionText(response.message);

          this.timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.clearLiveRegionText();
          }, 10000);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.setLiveRegionText(addedText);
            setTimeout(() => this.clearLiveRegionText(), 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  setLiveRegionText(text) {
    this.refs.liveRegion.textContent = text;
  }

  clearLiveRegionText() {
    this.refs.liveRegion.textContent = '';
  }

  onVariantUpdate(event) {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;
    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');
    const newComponent = event.detail.data.html.querySelector('product-form-component');

    if (!currentAddToCartButton) return;

    if (newComponent.dataset.onhold === 'true') {
      console.dir(newComponent.dataset.onhold);
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    }else if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    variantId.value = event.detail.resource.id ?? '';

    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }
  }

  onVariantSelected() {
    this.refs.addToCartButtonContainer?.disable();
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  connectedCallback() {
    this.animate();
  }

  animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offset = { x: rect.width / 2, y: rect.height / 2 };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };
    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 };
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 };

    let startTime = null;
    const duration = 600;

    this.style.opacity = '1';

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);
    requestAnimationFrame(animate);
  }
}

function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;
  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
